import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { hasPlanFeature } from '../lib/plans';
import { PlanUpgradeNotice } from '../components/PlanUpgradeNotice';
import { MapPin, Crown, TrendingUp } from 'lucide-react';

export default function StatsPage() {
    const { user, restaurant, myLocations } = useAuth();
    const [rev, setRev] = useState([]);
    const [rev30, setRev30] = useState([]);
    const [top, setTop] = useState([]);
    const [topTables, setTopTables] = useState([]);
    const [consolidated, setConsolidated] = useState(null);
    const [view, setView] = useState('this');   // 'this' | 'all'
    const [loadingConsol, setLoadingConsol] = useState(false);

    const statsAllowed = hasPlanFeature(restaurant, 'stats');
    const isChainOwner = user?.role === 'restaurant_admin' && (myLocations?.length || 0) > 1;

    useEffect(() => {
        if (!statsAllowed || view !== 'this') return;
        api.get('/r/stats/revenue?days=14').then(r => setRev(r.data.items)).catch(() => {});
        api.get('/r/stats/revenue?days=30').then(r => setRev30(r.data.items)).catch(() => {});
        api.get('/r/stats/top-products?days=30&limit=10').then(r => setTop(r.data.items)).catch(() => {});
        api.get('/r/stats/top-tables?days=30&limit=10').then(r => setTopTables(r.data.items)).catch(() => {});
    }, [statsAllowed, view]);

    useEffect(() => {
        if (!statsAllowed || !isChainOwner || view !== 'all') return;
        setLoadingConsol(true);
        api.get('/r/stats/consolidated?days=30')
            .then((r) => setConsolidated(r.data))
            .catch(() => setConsolidated(null))
            .finally(() => setLoadingConsol(false));
    }, [statsAllowed, isChainOwner, view]);

    if (!statsAllowed) {
        return (
            <div className="fade-in-up" data-testid="stats-page">
                <div className="kicker">Statistici</div>
                <h1 className="headline page-title">Date, nu presupuneri.</h1>
                <PlanUpgradeNotice featureLabel="Statistici avansate" />
            </div>
        );
    }

    return (
        <div className="fade-in-up" data-testid="stats-page">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'end', flexWrap: 'wrap', gap: 12, marginBottom: '1.4rem' }}>
                <div>
                    <div className="kicker">Statistici</div>
                    <h1 className="headline page-title" style={{ marginBottom: 0 }}>Date, nu presupuneri.</h1>
                </div>
                {isChainOwner && (
                    <div data-testid="stats-view-selector" style={{
                        display: 'inline-flex',
                        background: 'var(--surface-2)',
                        borderRadius: 10,
                        padding: 3,
                        border: '1px solid var(--border-strong)',
                    }}>
                        <button
                            type="button"
                            onClick={() => setView('this')}
                            className={`btn btn-sm ${view === 'this' ? 'btn-primary' : 'btn-ghost'}`}
                            data-testid="stats-view-this"
                            style={{ borderRadius: 8 }}
                        >
                            <MapPin size={13} /> {restaurant?.name || 'Această locație'}
                        </button>
                        <button
                            type="button"
                            onClick={() => setView('all')}
                            className={`btn btn-sm ${view === 'all' ? 'btn-primary' : 'btn-ghost'}`}
                            data-testid="stats-view-all"
                            style={{ borderRadius: 8 }}
                        >
                            <TrendingUp size={13} /> Toate locațiile ({myLocations.length})
                        </button>
                    </div>
                )}
            </div>

            {view === 'all' && isChainOwner ? (
                <ConsolidatedView data={consolidated} loading={loadingConsol} />
            ) : (
                <PerLocationView rev={rev} rev30={rev30} top={top} topTables={topTables} />
            )}
        </div>
    );
}

function ConsolidatedView({ data, loading }) {
    if (loading || !data) {
        return (
            <div className="panel" style={{ textAlign: 'center', padding: '3rem' }}>
                <div className="spinner" style={{ margin: '0 auto' }} />
            </div>
        );
    }

    const cons = data.consolidated || {};
    const byLoc = data.by_location || [];
    const maxRev = Math.max(1, ...byLoc.map((l) => l.revenue));
    const top = cons.top_products || [];
    const maxQty = Math.max(1, ...top.map((t) => t.qty));

    return (
        <div data-testid="stats-consolidated">
            {/* Top KPIs */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: 12, marginBottom: '1.2rem' }}>
                <KpiBox
                    label="Venit total (30z)"
                    value={`${(cons.revenue || 0).toLocaleString('ro-RO')} RON`}
                    accent="var(--amber)"
                />
                <KpiBox
                    label="Comenzi (30z)"
                    value={(cons.orders || 0).toLocaleString('ro-RO')}
                    accent="var(--neon-teal)"
                />
                <KpiBox
                    label="Comandă medie"
                    value={`${(cons.avg_order || 0).toLocaleString('ro-RO')} RON`}
                    accent="var(--neon-cyan)"
                />
                <KpiBox
                    label="Locații"
                    value={String(data.total_locations || 0)}
                    accent="var(--neon-magenta)"
                />
            </div>

            {cons.best_location && (
                <div className="panel" style={{
                    marginBottom: '1.2rem',
                    background: 'rgba(242,101,34,0.06)',
                    border: '1px solid rgba(242,101,34,0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                }} data-testid="stats-best-location">
                    <Crown size={20} style={{ color: 'var(--amber)', flexShrink: 0 }} />
                    <div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>
                            Cea mai performantă locație
                        </div>
                        <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>
                            {cons.best_location.name} —{' '}
                            <span style={{ color: 'var(--amber)' }}>
                                {(cons.best_location.revenue || 0).toLocaleString('ro-RO')} RON
                            </span>
                        </div>
                    </div>
                </div>
            )}

            {/* By location breakdown */}
            <div className="panel" style={{ marginBottom: '1.2rem' }}>
                <h3 className="headline" style={{ margin: '0 0 1rem 0', fontSize: 'clamp(1.05rem, 3vw, 1.3rem)' }}>
                    Venituri pe locații
                </h3>
                {byLoc.length === 0 && <div style={{ color: 'var(--text-muted)' }}>Nicio comandă plătită încă.</div>}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {byLoc.map((loc) => (
                        <div key={loc.id} data-testid={`stats-loc-row-${loc.id}`}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 4, fontSize: '0.9rem', flexWrap: 'wrap' }}>
                                <span style={{ fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                    <MapPin size={13} style={{ color: 'var(--amber)' }} />
                                    {loc.name}
                                    <span className={`badge ${loc.plan === 'pro' ? 'badge-amber' : 'badge-teal'}`} style={{ fontSize: '0.65rem' }}>
                                        {(loc.plan || '').toUpperCase()}
                                    </span>
                                </span>
                                <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                                    {loc.orders} cmd · avg {loc.avg_order.toLocaleString('ro-RO')} RON ·{' '}
                                    <strong style={{ color: 'var(--amber)' }}>{loc.revenue.toLocaleString('ro-RO')} RON</strong>
                                </span>
                            </div>
                            <div style={{ height: 8, background: 'rgba(255,255,255,0.05)', borderRadius: 99, overflow: 'hidden' }}>
                                <div style={{
                                    width: `${(loc.revenue / maxRev) * 100}%`,
                                    height: '100%',
                                    background: 'linear-gradient(to right, var(--amber), var(--neon-magenta))',
                                    transition: 'width 0.4s ease',
                                }} />
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Top sellers across chain */}
            <div className="panel">
                <h3 className="headline" style={{ margin: '0 0 1rem 0', fontSize: 'clamp(1.05rem, 3vw, 1.3rem)' }}>
                    Top produse pe lanț (30z)
                </h3>
                {top.length === 0 && <div style={{ color: 'var(--text-muted)' }}>Niciun produs vândut încă.</div>}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {top.map((t, i) => (
                        <div key={i}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 4, fontSize: '0.88rem' }}>
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }} title={t.name}>
                                    {t.name}
                                </span>
                                <span style={{ color: 'var(--text-muted)', flexShrink: 0, fontSize: '0.82rem' }}>
                                    {t.qty}× · {t.revenue.toFixed(0)} RON
                                </span>
                            </div>
                            <div style={{ height: 6, background: 'rgba(255,255,255,0.05)', borderRadius: 99, overflow: 'hidden' }}>
                                <div style={{ width: `${(t.qty / maxQty) * 100}%`, height: '100%', background: 'var(--neon-teal)' }} />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function KpiBox({ label, value, accent }) {
    return (
        <div className="panel" style={{
            padding: '0.9rem 1rem',
            borderLeft: `3px solid ${accent}`,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
        }}>
            <span className="kicker" style={{ color: 'var(--text-muted)' }}>{label}</span>
            <span style={{ fontSize: '1.5rem', fontWeight: 700, color: accent }}>{value}</span>
        </div>
    );
}

function PerLocationView({ rev, rev30, top, topTables }) {
    const maxRev = Math.max(1, ...rev.map(r => r.revenue));
    const maxRev30 = Math.max(1, ...rev30.map(r => r.revenue));
    const maxQty = Math.max(1, ...top.map(t => t.qty));
    const maxTableRev = Math.max(1, ...topTables.map(t => t.revenue));
    const showEverySecondLabel = rev.length > 10;

    return (
        <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))', gap: '1.2rem' }}>
                <div className="panel" style={{ minWidth: 0 }}>
                    <h3 className="headline" style={{ margin: '0 0 1rem 0', fontSize: 'clamp(1.05rem, 3vw, 1.3rem)' }}>
                        Venituri ultimele 14 zile
                    </h3>
                    {rev.length === 0 && <div style={{ color: 'var(--text-muted)' }}>Nicio comandă plătită încă.</div>}
                    {(() => {
                        const BAR_AREA_PX = 160;
                        const MIN_BAR_PX = 3;
                        const hasAny = rev.some((r) => r.revenue > 0);
                        return (
                            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4 }}>
                                {rev.map((r, i) => {
                                    const pct = hasAny ? (r.revenue / maxRev) : 0;
                                    const barPx = Math.max(MIN_BAR_PX, Math.round(pct * BAR_AREA_PX));
                                    return (
                                        <div key={r.date} style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                                            <div style={{
                                                width: '100%',
                                                background: r.revenue > 0
                                                    ? 'linear-gradient(to top, var(--amber), var(--neon-magenta))'
                                                    : 'rgba(255,255,255,0.08)',
                                                borderRadius: '4px 4px 0 0',
                                                height: `${barPx}px`,
                                                transition: 'height 0.4s ease',
                                            }} title={`${r.date}: ${r.revenue.toFixed(0)} RON`} />
                                            <div style={{
                                                fontSize: '0.62rem',
                                                color: 'var(--text-faint)',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                maxWidth: '100%',
                                                visibility: showEverySecondLabel && i % 2 !== 0 ? 'hidden' : 'visible',
                                            }}>
                                                {r.date.slice(5)}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })()}
                    {rev.length > 0 && (
                        <div style={{ marginTop: 10, fontSize: '0.78rem', color: 'var(--text-muted)', textAlign: 'right' }}>
                            Total: <strong style={{ color: 'var(--amber)' }}>{rev.reduce((s, r) => s + r.revenue, 0).toFixed(0)} RON</strong>
                        </div>
                    )}
                </div>

                <div className="panel" style={{ minWidth: 0 }}>
                    <h3 className="headline" style={{ margin: '0 0 1rem 0', fontSize: 'clamp(1.05rem, 3vw, 1.3rem)' }}>
                        Top produse
                    </h3>
                    {top.length === 0 && <div style={{ color: 'var(--text-muted)' }}>Nu sunt vânzări încă.</div>}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {top.map((t, i) => (
                            <div key={i} style={{ minWidth: 0 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 4, fontSize: '0.88rem', minWidth: 0 }}>
                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }} title={t.name}>
                                        {t.name}
                                    </span>
                                    <span style={{ color: 'var(--text-muted)', flexShrink: 0, fontSize: '0.82rem' }}>
                                        {t.qty}× · {t.revenue.toFixed(0)} RON
                                    </span>
                                </div>
                                <div style={{ height: 6, background: 'rgba(255,255,255,0.05)', borderRadius: 99, overflow: 'hidden' }}>
                                    <div style={{ width: `${(t.qty / maxQty) * 100}%`, height: '100%', background: 'var(--neon-teal)' }} />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))', gap: '1.2rem', marginTop: '1.2rem' }}>
                <div className="panel" style={{ minWidth: 0 }}>
                    <h3 className="headline" style={{ margin: '0 0 1rem 0', fontSize: 'clamp(1.05rem, 3vw, 1.3rem)' }}>
                        Venituri ultimele 30 zile
                    </h3>
                    {rev30.length === 0 && <div style={{ color: 'var(--text-muted)' }}>Nicio comandă plătită încă.</div>}
                    {rev30.length > 0 && (() => {
                        const BAR_AREA_PX = 160;
                        const MIN_BAR_PX = 2;
                        const hasAny = rev30.some((r) => r.revenue > 0);
                        return (
                            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2 }}>
                                {rev30.map((r, i) => {
                                    const pct = hasAny ? (r.revenue / maxRev30) : 0;
                                    const barPx = Math.max(MIN_BAR_PX, Math.round(pct * BAR_AREA_PX));
                                    return (
                                        <div key={r.date} style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                                            <div style={{
                                                width: '100%',
                                                background: r.revenue > 0
                                                    ? 'linear-gradient(to top, var(--neon-cyan), var(--brand-teal-light))'
                                                    : 'rgba(255,255,255,0.08)',
                                                borderRadius: '3px 3px 0 0',
                                                height: `${barPx}px`,
                                                transition: 'height 0.4s ease',
                                            }} title={`${r.date}: ${r.revenue.toFixed(0)} RON`} />
                                            <div style={{
                                                fontSize: '0.55rem',
                                                color: 'var(--text-faint)',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                maxWidth: '100%',
                                                visibility: i % 5 !== 0 ? 'hidden' : 'visible',
                                            }}>
                                                {r.date.slice(5)}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })()}
                    {rev30.length > 0 && (
                        <div style={{ marginTop: 10, fontSize: '0.78rem', color: 'var(--text-muted)', textAlign: 'right' }}>
                            Total 30 zile: <strong style={{ color: 'var(--neon-cyan)' }}>{rev30.reduce((s, r) => s + r.revenue, 0).toFixed(0)} RON</strong>
                        </div>
                    )}
                </div>

                <div className="panel" style={{ minWidth: 0 }}>
                    <h3 className="headline" style={{ margin: '0 0 1rem 0', fontSize: 'clamp(1.05rem, 3vw, 1.3rem)' }}>
                        Top mese (30 zile)
                    </h3>
                    {topTables.length === 0 && <div style={{ color: 'var(--text-muted)' }}>Nu sunt comenzi plătite încă.</div>}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {topTables.map((t, i) => (
                            <div key={i} style={{ minWidth: 0 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 4, fontSize: '0.88rem', minWidth: 0 }}>
                                    <span style={{ flexShrink: 0, fontWeight: 600 }}>
                                        🪑 Masa {t.table_number}
                                    </span>
                                    <span style={{ color: 'var(--text-muted)', flexShrink: 0, fontSize: '0.82rem' }}>
                                        {t.orders} comenzi · <strong style={{ color: 'var(--amber)' }}>{t.revenue.toFixed(0)} RON</strong>
                                    </span>
                                </div>
                                <div style={{ height: 6, background: 'rgba(255,255,255,0.05)', borderRadius: 99, overflow: 'hidden' }}>
                                    <div style={{
                                        width: `${(t.revenue / maxTableRev) * 100}%`,
                                        height: '100%',
                                        background: 'linear-gradient(to right, var(--amber), var(--neon-magenta))',
                                    }} />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </>
    );
}
